import qs from 'query-string';
import superagent, { SuperAgent, SuperAgentRequest, Request } from 'superagent';
import {
  HttpDeleteConfig,
  HttpGetConfig,
  HttpHeadConfig,
  HttpMutationConfig,
  RequestConfig,
  ResponseHeaders,
  RestClient,
} from 'types';
import { defaultToUndefined } from 'utils/lang';
import authHeaderRequestPlugin from './authHeaderRequestPlugin';

function applyQueryParams(req: Request, params?: Record<string, unknown>) {
  return params && Object.keys(params).length > 0
    ? req.query(qs.stringify(params))
    : req;
}

export default class SuperAgentClient implements RestClient {
  private request: SuperAgent<SuperAgentRequest>;

  constructor() {
    this.request = superagent.agent().use(authHeaderRequestPlugin());
  }

  private registerCancellationToken(
    req: Request,
    config: RequestConfig | undefined,
  ) {
    const { signal } = config ?? {};

    if (signal) {
      signal.addEventListener('abort', () => {
        req.abort();
      });
    }
  }

  private setResponseType(req: Request, config: RequestConfig | undefined) {
    const { responseType } = config ?? {};
    if (responseType === 'blob') {
      req.responseType('blob');
    }
  }

  async get<R>(url: string, config?: HttpGetConfig): Promise<R> {
    const { params, withCredentials } = config ?? {};

    const req = applyQueryParams(this.request.get(url), params).authHeader(
      config,
    );

    if (withCredentials) {
      req.withCredentials();
    }

    this.registerCancellationToken(req, config);

    this.setResponseType(req, config);

    const { body } = await req;

    return body;
  }

  async post<R, D extends object | undefined = undefined>(
    url: string,
    config: HttpMutationConfig<D>,
  ): Promise<R> {
    const { data: payload = {}, params } = config;

    const req = applyQueryParams(this.request.post(url), params)
      .send(payload)
      .authHeader(config);

    this.registerCancellationToken(req, config);

    this.setResponseType(req, config);

    const { body } = await req;

    return body;
  }

  async put<R, D extends object | undefined = undefined>(
    url: string,
    config: HttpMutationConfig<D>,
  ): Promise<R> {
    const { data: payload = {}, params } = config;

    const req = applyQueryParams(this.request.put(url), params)
      .send(payload)
      .authHeader(config);

    this.registerCancellationToken(req, config);

    this.setResponseType(req, config);

    const { body } = await req;

    return body;
  }

  async delete<R>(url: string, config: HttpDeleteConfig = {}): Promise<R> {
    const { params } = config;

    const req = applyQueryParams(this.request.delete(url), params).authHeader(
      config,
    );

    this.registerCancellationToken(req, config);

    this.setResponseType(req, config);

    const { body } = await req;

    return body;
  }

  async head(
    url: string,
    config: HttpHeadConfig = {},
  ): Promise<ResponseHeaders> {
    const { params } = config;
    const req = applyQueryParams(this.request.head(url), params).authHeader(
      config,
    );

    this.registerCancellationToken(req, config);

    this.setResponseType(req, config);

    const result = await req;

    const headers = {
      contentLength: defaultToUndefined(result.header['content-length']),
    };

    return headers;
  }
}
